import { useEffect, useMemo, useRef, useState } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { DecalGeometry } from 'three-stdlib'
import { useDecalStore } from '../stores/useDecalStore'
import type { DecalData, DecalBlendMode } from '../types/decal'
import { debug, debugWarn, debugError } from '../utils/logger'

/**
 * Get Three.js blend mode constant from string
 */
const getBlendMode = (mode: DecalBlendMode): THREE.Blending => {
  switch (mode) {
    case 'normal':
      return THREE.NormalBlending
    case 'additive':
      return THREE.AdditiveBlending
    case 'multiply':
      return THREE.MultiplyBlending
    case 'overlay':
      return THREE.NormalBlending // Three.js doesn't have overlay, use custom shader or normal
    default:
      return THREE.NormalBlending
  }
}

/**
 * Individual decal mesh component
 * Renders a single DecalGeometry projected onto a target mesh
 */
interface DecalMeshProps {
  decal: DecalData
  targetMesh: THREE.Mesh | null
}

function DecalMesh({ decal, targetMesh }: DecalMeshProps) {
  const meshRef = useRef<THREE.Mesh>(null)
  const textureRef = useRef<THREE.Texture | null>(null)
  const [textureAspect, setTextureAspect] = useState<number>(decal.aspectRatio || 1)

  // Load texture
  useEffect(() => {
    debug('Decals', `Loading texture for decal ${decal.id}: ${decal.textureUrl}`)
    const loader = new THREE.TextureLoader()
    loader.load(
      decal.textureUrl,
      (texture) => {
        debug('Decals', `Texture loaded successfully for decal ${decal.id}`)
        textureRef.current = texture

        // Calculate and store aspect ratio
        if (texture.image) {
          const aspect = texture.image.width / texture.image.height
          setTextureAspect(aspect)
          debug(
            'Decals',
            `Texture aspect ratio: ${aspect} (${texture.image.width}x${texture.image.height})`
          )
        }

        if (meshRef.current) {
          const material = meshRef.current.material as THREE.MeshStandardMaterial
          material.map = texture
          material.color.set(0xffffff) // Reset color from placeholder
          material.needsUpdate = true
        }
      },
      (progress) => {
        debug(
          'Decals',
          `Loading texture ${decal.id}: ${((progress.loaded / progress.total) * 100).toFixed(0)}%`
        )
      },
      (error) => {
        debugError(
          'Decals',
          `Error loading texture for decal ${decal.id}:`,
          decal.textureUrl,
          error
        )
      }
    )

    return () => {
      if (textureRef.current) {
        textureRef.current.dispose()
      }
    }
  }, [decal.textureUrl, decal.id])

  // Create decal geometry
  const geometry = useMemo(() => {
    if (!targetMesh) {
      debugWarn('Decals', `No target mesh for decal ${decal.id}`)
      return null
    }

    try {
      // CRITICAL: Update mesh world matrix before creating decal geometry
      targetMesh.updateMatrixWorld(true)

      // Validate mesh has geometry
      if (!targetMesh.geometry) {
        debugError('Decals', `Target mesh has no geometry: ${targetMesh.name || targetMesh.uuid}`)
        return null
      }

      // Create decal geometry with aspect ratio correction
      const position = new THREE.Vector3(decal.position.x, decal.position.y, decal.position.z)
      const rotation = new THREE.Euler(decal.rotation.x, decal.rotation.y, decal.rotation.z)

      // Apply aspect ratio to the scale
      const baseScale = decal.scale.x // Use X as the uniform scale
      const size = new THREE.Vector3(
        baseScale * textureAspect, // Width adjusted by aspect ratio
        baseScale, // Height
        baseScale // Depth (same as height for decals)
      )

      debug('Decals', `Creating decal ${decal.id}:`, {
        meshName: targetMesh.name || 'unnamed',
        position: position.toArray(),
        size: size.toArray(),
      })

      const decalGeometry = new DecalGeometry(targetMesh, position, rotation, size)

      // Validate the created geometry
      if (!decalGeometry.attributes.position || decalGeometry.attributes.position.count === 0) {
        debugError('Decals', `Created geometry has no vertices for decal ${decal.id}`)
        return null
      }

      debug(
        'Decals',
        `Created decal geometry with ${decalGeometry.attributes.position.count} vertices`
      )

      return decalGeometry
    } catch (error) {
      debugError('Decals', `Error creating decal geometry for ${decal.id}:`, error)
      return null
    }
  }, [targetMesh, decal.position, decal.rotation, decal.scale, decal.id, textureAspect])

  // Create material
  const material = useMemo(() => {
    const mat = new THREE.MeshStandardMaterial({
      transparent: true,
      opacity: decal.opacity,
      depthTest: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -4,
      blending: getBlendMode(decal.blendMode),
      side: THREE.DoubleSide, // Render both sides to ensure visibility
      toneMapped: false, // Prevent tone mapping affecting decal colors
    })

    if (textureRef.current) {
      mat.map = textureRef.current
      mat.needsUpdate = true
      debug('Decals', `Material has texture for decal ${decal.id}`)
    } else {
      // Show a placeholder color if texture hasn't loaded yet
      mat.color.set(0xff00ff) // Magenta to make missing textures obvious
      debugWarn('Decals', `Material has no texture yet for decal ${decal.id}`)
    }

    return mat
  }, [decal.opacity, decal.blendMode, decal.id])

  // Update material when opacity or blend mode changes
  useEffect(() => {
    if (meshRef.current) {
      const mat = meshRef.current.material as THREE.MeshStandardMaterial
      mat.opacity = decal.opacity
      mat.blending = getBlendMode(decal.blendMode)
      mat.needsUpdate = true
    }
  }, [decal.opacity, decal.blendMode])

  // Cleanup
  useEffect(() => {
    return () => {
      if (geometry) {
        geometry.dispose()
      }
      material.dispose()
    }
  }, [geometry, material])

  if (!geometry) {
    debugWarn('Decals', `No geometry for decal ${decal.id}, skipping render`)
    return null
  }

  if (!targetMesh) {
    debugWarn('Decals', `No target mesh for decal ${decal.id}, skipping render`)
    return null
  }

  return (
    <mesh
      ref={meshRef}
      geometry={geometry}
      material={material}
      renderOrder={1}
      frustumCulled={true}
    />
  )
}

// Pre-allocated objects for raycasting (avoids per-decal GC pressure)
const _raycaster = new THREE.Raycaster()
const _position = new THREE.Vector3()
const _normal = new THREE.Vector3()
const _rayOrigin = new THREE.Vector3()
const _rayDirection = new THREE.Vector3()

/** Raycast all decals to find their target meshes */
function findDecalMeshes(decals: DecalData[], scene: THREE.Scene): Map<string, THREE.Mesh> {
  const meshMap = new Map<string, THREE.Mesh>()

  decals.forEach((decal) => {
    _position.set(decal.position.x, decal.position.y, decal.position.z)
    _normal.set(decal.normal.x, decal.normal.y, decal.normal.z)

    // Raycast from slightly above the surface back into it
    _rayOrigin.copy(_position).addScaledVector(_normal, 0.01)
    _rayDirection.copy(_normal).negate()

    _raycaster.set(_rayOrigin, _rayDirection)
    const intersects = _raycaster.intersectObjects(scene.children, true)

    for (const intersect of intersects) {
      if (intersect.object instanceof THREE.Mesh) {
        meshMap.set(decal.id, intersect.object)
        break
      }
    }
  })

  return meshMap
}

/**
 * Decals component - renders all decals in the scene
 * Uses SPATIAL APPROACH: Raycast from saved position/normal to find target mesh
 * This is more robust than tracking mesh IDs which can change
 */
export default function Decals() {
  const { scene } = useThree()
  const decals = useDecalStore((state) => state.decals)
  const loadDefaultConfiguration = useDecalStore((state) => state.loadDefaultConfiguration)

  // Load default decal configuration on mount
  // This ensures decals are loaded even when DecalEditor is not mounted (dev mode locked)
  useEffect(() => {
    loadDefaultConfiguration()
  }, [loadDefaultConfiguration])

  // Store target meshes found via raycasting
  const [decalMeshMap, setDecalMeshMap] = useState<Map<string, THREE.Mesh>>(new Map())

  // Find target meshes via raycasting when decals change
  // Includes a delayed retry for decals that load before the 3D scene is ready
  useEffect(() => {
    if (decals.length === 0) {
      setDecalMeshMap(new Map())
      return
    }

    // Initial raycast
    const meshMap = findDecalMeshes(decals, scene)
    setDecalMeshMap(meshMap)

    // If some decals didn't find a mesh, retry once after scene has had time to load
    const unmatchedCount = decals.length - meshMap.size
    if (unmatchedCount > 0) {
      const retryTimer = setTimeout(() => {
        const retryMap = findDecalMeshes(decals, scene)
        setDecalMeshMap(retryMap)
      }, 2000)
      return () => clearTimeout(retryTimer)
    }
  }, [scene, decals])

  return (
    <>
      {decals.map((decal) => {
        const targetMesh = decalMeshMap.get(decal.id) || null

        if (!targetMesh) {
          return null
        }

        return <DecalMesh key={decal.id} decal={decal} targetMesh={targetMesh} />
      })}
    </>
  )
}
